import { useState, useCallback, useEffect, useMemo } from 'react';
import { isEqual, merge } from 'lodash';
import clsx from 'clsx';
import { Box } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Trans } from '@lingui/macro';

import { useStyles } from './PerformanceChartStyles';
import { getLogger } from '../../../utilities/UseLog';
import Chart from '../chart/Chart';
import MessageOverlay from '../message-overlay/MessageOverlay';
import {
    getPlotOptions,
    getChart,
    getXAxis,
    getYAxis,
    getStatusBands,
    getSeries,
    getGranularity,
    getSelectionBounds,
    hightLightPoints,
    getNumberPoints,
    processData,
} from './funcs';
import { GetTooltip } from '../tooltip/GetTooltip';
import Tooltip from '../../tooltip/Tooltip';
import { CUSTOM_ID } from '../../filters/time-range-filter/TimeRangeFilterConstants';
import { useFilterState } from '../../../stores/FilterStateStore';
import { usePageFilterState } from '../../page-framework/panel/Panel';
import { filterId as timeRangeFilterId } from '../../filters/time-range-filter/TimeRangeFilter';
import { getPointWidth } from '../data-series';

const log = getLogger('PerformanceChart');

export default function PerformanceChart({
    chartDef,
    className,
    data: rawData,
    initialHeight,
    disableLegend,
    disableYAxis,
    disableTimeAxis,
    handleEvents,
    onSetWidths,
    opacity,
    pointWidth,
    selectedDate,
    xAxis,
    yAxisTop,
    hideLine,
    chartRef,
}) {
    const theme = useTheme();
    const [inSelection, setInSelection] = useState(false);
    const [persistChart, setPersistChart] = useState();
    const [overPoint, setOverPoint] = useState(false);
    const [modChartDef, setModChartDef] = useState(null);
    const [timeslice, setTimeslice] = useState();
    const [widths, setWidths] = useState({});
    const [filterState] = useFilterState();
    const timeRange = filterState?.current?.[timeRangeFilterId];
    const { start, end } = timeRange || {};

    const enableLegend = !disableLegend;
    const granularity = getGranularity(chartDef, rawData);
    const statusMetric = chartDef?.statusMetric;
    const yAxis = chartDef?.yAxis;
    const classes = useStyles();

    const showTooltip = useMemo(() => {
        return !inSelection && overPoint && timeslice;
    }, [inSelection, overPoint, timeslice]);

    useEffect(() => {
        if (!isEqual(modChartDef, chartDef)) {
            setModChartDef(chartDef);
        }
    }, [chartDef, modChartDef]);

    // Populate aggregated metrics, if any
    const { processedData: data } = processData(modChartDef, rawData);

    const series = useMemo(
        () =>
            getSeries(
                modChartDef,
                data,
                getGranularity(modChartDef, data),
                // eslint-disable-next-line no-shadow
                (overPoint, timeslice) => {
                    setOverPoint(overPoint);
                    if (overPoint) setTimeslice(timeslice);
                },
                handleEvents,
                selectedDate
            ),
        [modChartDef, data, handleEvents, selectedDate]
    );

    const [, /* Not used */ filterStateActions] = usePageFilterState();

    // selection is the sweep-n-swoop (drag 'n zoom) for a chart. Prevent default Highcharts behavior,
    // and round to the nearest granularity based on the selection. From there, set the timerange to
    // that value.
    const chartSelectionCallback = useCallback(
        (event) => {
            const bounds = getSelectionBounds(event, granularity);
            filterStateActions.setFilterState(timeRangeFilterId, { id: CUSTOM_ID, ...bounds });
            setInSelection(false);
            event.preventDefault();
            return false;
        },
        [granularity, filterStateActions]
    );

    useEffect(() => {
        if (!timeslice) return;
        hightLightPoints(overPoint, timeslice);
    }, [overPoint, timeslice]);

    const aChart = useMemo(() => {
        return {
            ...getChart(modChartDef, disableYAxis, chartSelectionCallback),
            backgroundColor: theme.palette.background.paper,
            className,
        };
    }, [modChartDef, disableYAxis, chartSelectionCallback, className, theme]);

    const plotOptions = useMemo(() => {
        return getPlotOptions(modChartDef, data, pointWidth, opacity);
    }, [modChartDef, data, pointWidth, opacity]);

    const numDataPoints = useMemo(() => {
        return data && modChartDef ? getNumberPoints(modChartDef, data) : 0;
    }, [modChartDef, data]);

    const adjustedXAxis = useMemo(() => {
        const plotBands = getStatusBands(data, statusMetric);
        const noData = numDataPoints === 0;
        return getXAxis(disableTimeAxis || noData, plotBands, xAxis, hideLine, theme.palette.type);
    }, [data, statusMetric, numDataPoints, theme.palette.type, disableTimeAxis, xAxis, hideLine]);

    const adjustedYAxis = useMemo(() => {
        return getYAxis(disableYAxis, yAxis, yAxisTop, theme.palette.type);
    }, [disableYAxis, theme.palette.type, yAxis, yAxisTop]);

    const legendCursor = modChartDef?.legendCursor !== false;
    const options = useMemo(() => {
        const legend = merge(
            {
                enabled: enableLegend,
                backgroundColor: theme.palette.background.paper,
                alignColumns: false,
            },
            legendCursor
                ? {}
                : {
                      itemHoverStyle: {
                          color: theme.palette.type === 'dark' ? '#E0E0E3' : '#333333',
                      },
                      itemStyle: {
                          cursor: 'default',
                      },
                  }
        );
        return {
            chart: aChart,
            credits: {
                enabled: false,
            },
            legend,
            plotOptions,
            series,
            seriesDefaults: {
                mappingDefaults: {
                    xMapping: 'date',
                    yMapping: 'value',
                },
                noSharedTooltip: false,
                visible: true,
            },
            title: {
                enabled: false,
                text: null,
            },
            tooltip: {
                enabled: false,
            },
            xAxis: adjustedXAxis,
            yAxis: adjustedYAxis,
        };
    }, [aChart, enableLegend, plotOptions, series, adjustedXAxis, adjustedYAxis, theme, legendCursor]);

    log.debug('PerformanceChart options:');
    log.debugTable(options);

    useEffect(() => {
        if (!persistChart?.container) return;

        const down = persistChart.container.onmousedown;
        persistChart.container.onmousedown = (ev) => {
            if (down) down(ev);
            setInSelection(true);
        };

        const up = persistChart.container.onmouseup;
        persistChart.container.onmouseup = (ev) => {
            if (up) up(ev);
            setInSelection(false);
        };
    }, [persistChart]);

    const handleResize = useCallback(
        (chart) => {
            const newWidth = chart?.chartWidth;
            const newPointWidth = getPointWidth(modChartDef, data, newWidth, { start, end });

            setPersistChart((currentChart) => (currentChart !== chart ? chart : currentChart));

            if (newWidth !== widths.chartWidth || newPointWidth !== widths.pointWidth) {
                const newWidths = {
                    chart,
                    chartWidth: newWidth,
                    pointWidth: newPointWidth,
                };
                setWidths(newWidths);
                onSetWidths(newWidths);
            }
        },
        [widths.chartWidth, widths.pointWidth, data, modChartDef, onSetWidths, start, end]
    );

    if (!data || !modChartDef) return <Box height={initialHeight} />;

    return (
        <div className={clsx('flex-container', classes.chartContainer)}>
            {numDataPoints === 0 && <MessageOverlay message={<Trans>No data available</Trans>} />}
            {numDataPoints > 0 && (
                <Tooltip
                    open={!!showTooltip}
                    offset={60}
                    placement="left"
                    title={<GetTooltip chartDef={modChartDef} timeslice={timeslice} />}
                >
                    <Chart
                        className={clsx('flex-container', classes.chart)}
                        initialHeight={initialHeight}
                        options={options}
                        onResize={handleResize}
                        chartRef={chartRef}
                    />
                </Tooltip>
            )}
        </div>
    );
}

PerformanceChart.defaultProps = {
    opacity: 1,
    handleEvents: {},
    className: undefined,
    xAxis: undefined,
    yAxisTop: undefined,
    onSetWidths: () => {},
};
